import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  isQueryStringOptionFromFilter,
  transformWhereClauseToFieldNameValues,
} from '@modules/common/utils';
import { useFetchEQLReferences } from '@modules/queryBuilder/hooks';
import {
  LinearProgress,
  Stack,
  SxProps,
  Theme,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/system';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import { NestedFilterPreview } from './NestedFilterPreview';
import { SimpleFilterPreview } from './SimpleFilterPreview';

export interface WhereClausePreviewProps {
  whereClause?: WhereClause | null;
  sxProps?: SxProps<Theme>;
  shouldHideTooltips?: boolean;
  isLoading?: boolean;
  helperText?: string;
  isError?: boolean;
  valueTextColor?: string;
  headerNode?: React.ReactNode;
}

export default function WhereClausePreview({
  whereClause,
  sxProps,
  shouldHideTooltips = false,
  isLoading: isLoadingProp = false,
  helperText,
  isError,
  valueTextColor = 'primary.main',
  headerNode,
}: WhereClausePreviewProps) {
  const theme = useTheme();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const whereClauseToPreview = whereClause ?? EmptyWhereClause;

  const { eqlFieldByID, isLoading: fetchEQLReferenceInProgress } =
    useFetchEQLReferences({
      whereClause: whereClauseToPreview,
      fetchPolicy: 'cache-and-network',
      skip: !whereClause,
    });

  const formattedQueryStringOptions = transformWhereClauseToFieldNameValues(
    whereClauseToPreview,
    eqlFieldByID,
    isUnifiedFieldEnabled,
    shouldRemoveFileUploadPrefix,
  );

  const isLoading = fetchEQLReferenceInProgress || isLoadingProp;

  const renderOptions = () => {
    if (helperText) {
      return (
        <Typography
          variant="subtitle2"
          color={isError ? 'error' : 'text.disabled'}
          p={1}
        >
          {helperText}
        </Typography>
      );
    }

    if (!formattedQueryStringOptions?.length) {
      return (
        <Typography variant="subtitle2" color="text.disabled" p={1}>
          No filters applied
        </Typography>
      );
    }

    return formattedQueryStringOptions?.map(
      (formattedQueryStringOption, index) => {
        if (!formattedQueryStringOption) {
          return null;
        }

        const isSimpleFilter = isQueryStringOptionFromFilter(
          formattedQueryStringOption,
        );

        return isSimpleFilter ? (
          <SimpleFilterPreview
            key={index}
            filterData={formattedQueryStringOption}
            conjunction={index > 0 ? whereClauseToPreview.op?.type : undefined}
            fieldDisplayName={formattedQueryStringOption.fieldDisplayName}
            shouldHideTooltips={shouldHideTooltips}
            valueTextColor={valueTextColor}
          />
        ) : (
          <NestedFilterPreview
            key={index}
            filterData={formattedQueryStringOption}
            conjunction={index > 0 ? whereClauseToPreview.op?.type : undefined}
            shouldHideTooltips={shouldHideTooltips}
            valueTextColor={valueTextColor}
          />
        );
      },
    );
  };

  return (
    <Stack
      sx={{
        background: alpha(theme.palette.primary.main, 0.05),
        py: 0.5,
        px: 1.5,
        maxHeight: '150px',
        overflow: 'auto',
        borderRadius: 1,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.2),
        ...sxProps,
      }}
      className="where-clause-preview"
    >
      {headerNode}
      {isLoading ? (
        <LinearProgress
          variant="indeterminate"
          sx={{
            borderRadius: 4,
            my: 3,
          }}
        />
      ) : (
        renderOptions()
      )}
    </Stack>
  );
}
